<template>
  <div class="forgot-password-container">
    <div class="form-card">
      <!-- Step 1: Enter Email -->
      <div v-if="currentStep === 1" class="form-content">
        <h2 class="form-title">Forgot Password?</h2>
        <p class="form-subtitle">Enter your email address and we'll send you a link to reset your password.</p>
        
        <form @submit.prevent="sendPasswordReset" class="form">
          <div class="input-group">
            <input
              type="email"
              v-model="email"
              placeholder="Enter your email"
              class="form-input"
              :disabled="loading"
              required
            />
          </div>
          
          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>
          
          <button type="submit" class="submit-btn" :disabled="loading">
            <span v-if="loading" class="loading-spinner"></span>
            {{ loading ? 'Sending...' : 'Send Reset Link' }}
          </button>
        </form>
        
        <div class="form-footer">
          <a href="#" class="link" @click="goBack">← Back to Login</a>
        </div>
      </div>

      <!-- Step 2: Email Sent Confirmation -->
      <div v-if="currentStep === 2" class="form-content">
        <div class="success-content">
          <div class="email-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </div>
          <h2 class="form-title">Check Your Email</h2>
          <p class="form-subtitle">We've sent a password reset link to <strong>{{ email }}</strong>. Click the link in the email to reset your password.</p>
          
          <div class="action-buttons">
            <button @click="resendPasswordReset" class="resend-btn" :disabled="resendLoading">
              <span v-if="resendLoading" class="loading-spinner"></span>
              {{ resendLoading ? 'Sending...' : 'Resend Email' }}
            </button>
            
            <button @click="goToLogin" class="submit-btn">
              Back to Login
            </button>
          </div>
          
          <div v-if="resendMessage" class="success-text">
            {{ resendMessage }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from '../../db.js'

export default {
  name: 'ForgotPassword',
  data() {
    return {
      currentStep: 1,
      email: '',
      loading: false,
      resendLoading: false,
      errorMessage: '',
      resendMessage: ''
    }
  },
  methods: {
    async sendPasswordReset() {
      if (!this.email) {
        this.errorMessage = 'Please enter your email address';
        return;
      }

      this.loading = true;
      this.errorMessage = '';

      try {
        await firebase.auth().sendPasswordResetEmail(this.email);
        this.currentStep = 2;
        console.log('Password reset email sent to:', this.email);
      } catch (error) {
        console.error('Error sending password reset email:', error);
        this.handleFirebaseError(error);
      } finally {
        this.loading = false;
      }
    },

    async resendPasswordReset() {
      this.resendLoading = true;
      this.resendMessage = '';

      try {
        await firebase.auth().sendPasswordResetEmail(this.email);
        this.resendMessage = 'Password reset email sent again!';
        console.log('Password reset email resent to:', this.email);
        
        // Clear the success message after 3 seconds
        setTimeout(() => {
          this.resendMessage = '';
        }, 3000);
      } catch (error) {
        console.error('Error resending password reset email:', error);
        this.resendMessage = 'Error sending email. Please try again.';
      } finally {
        this.resendLoading = false;
      }
    },

    handleFirebaseError(error) {
      switch (error.code) {
        case 'auth/user-not-found':
          this.errorMessage = 'No account found with this email address';
          break;
        case 'auth/invalid-email':
          this.errorMessage = 'Please enter a valid email address';
          break;
        case 'auth/too-many-requests':
          this.errorMessage = 'Too many requests. Please wait a moment and try again';
          break;
        case 'auth/network-request-failed':
          this.errorMessage = 'Network error. Please check your connection and try again';
          break;
        default:
          this.errorMessage = 'An error occurred. Please try again';
          break;
      }
    },

    goBack() {
      // Navigate back to login page
      this.$router.push('/login');
    },

    goToLogin() {
      // Navigate to login page
      this.$router.push('/login');
    }
  }
}
</script>

<style scoped>
.forgot-password-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #FE8F4D 0%, #5226DE 100%);
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.form-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 580px;
  overflow: hidden;
}

.form-content {
  padding: 78px;
}

.form-title {
  font-size: 40px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  text-align: center;
}

.form-subtitle {
  color: #666;
  font-size: 21px;
  text-align: center;
  margin-bottom: 40px;
  line-height: 1.5;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.input-group {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 18px;
  border: 2px solid #e1e5e9;
  border-radius: 10px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
}

.form-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.submit-btn, .resend-btn {
  background: #00003C;
  color: white;
  border: none;
  padding: 18px;
  border-radius: 10px;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.submit-btn:hover, .resend-btn:hover {
  opacity: 0.9;
}

.submit-btn:disabled, .resend-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.resend-btn {
  background: transparent;
  color: #667eea;
  border: 2px solid #667eea;
  margin-bottom: 16px;
}

.resend-btn:hover {
  background: #667eea;
  color: white;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.form-footer {
  margin-top: 32px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.link {
  color: #ff7b54;
  text-decoration: none;
  font-size: 17px;
  transition: color 0.3s ease;
}

.link:hover {
  color: #667eea;
  text-decoration: underline;
}

.success-content {
  text-align: center;
}

.email-icon {
  color: #667eea;
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 32px;
}

.error-message {
  background-color: #fee;
  color: #c53030;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #fed7d7;
  font-size: 14px;
  text-align: center;
}

.success-text {
  background-color: #f0fff4;
  color: #38a169;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #c6f6d5;
  font-size: 14px;
  text-align: center;
  margin-top: 16px;
}

@media (max-width: 480px) {
  .form-content {
    padding: 32px 24px;
  }
  
  .form-title {
    font-size: 24px;
  }
  
  .form-subtitle {
    font-size: 18px;
  }
}</style>


// WEBPACK FOOTER //
// src/components/layout/ForgotPassword.vue